import { supabase, isSupabaseConfigured } from "@/lib/supabaseClient";
import { adminService } from "@/services/adminService";

export interface UploadedMedia {
  bucket: "question-images" | "question-audios";
  path: string;
  url: string;
  fileName: string;
  mimeType: string;
  size: number;
}

// Helper to clean and sanitize filenames for storage safety
function getSafeFileName(fileName: string): string {
  return fileName
    .normalize("NFD") // split accent characters
    .replace(/[\u0300-\u036f]/g, "") // remove accents
    .replace(/[^a-zA-Z0-9._-]/g, "-") // sanitize to safe characters
    .toLowerCase();
}

// Safety limits
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_AUDIO_SIZE = 50 * 1024 * 1024; // 50MB

const ALLOWED_IMAGE_EXTS = [".jpg", ".jpeg", ".png", ".webp"];
const ALLOWED_AUDIO_EXTS = [".mp3", ".wav", ".ogg", ".m4a", ".aac"];

export const storageService = {
  /**
   * Upload an image to the 'question-images' bucket.
   * Performs client-side format and size checks before uploading.
   */
  async uploadQuestionImage(file: File, questionIdOrTempId?: string): Promise<UploadedMedia> {
    if (!isSupabaseConfigured || !supabase) {
      throw new Error("Supabase não está configurado. Não é possível fazer upload.");
    }

    // 1. Validation check
    const extension = "." + file.name.split(".").pop()?.toLowerCase();
    const isValidType = ALLOWED_IMAGE_EXTS.includes(extension) || file.type.startsWith("image/");
    if (!isValidType) {
      throw new Error(`Formato de imagem inválido. Use apenas: ${ALLOWED_IMAGE_EXTS.join(", ")}`);
    }

    if (file.size > MAX_IMAGE_SIZE) {
      throw new Error(`Imagem excede o tamanho máximo de 5MB. Tamanho do arquivo: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
    }

    return this.uploadFile(file, "question-images", questionIdOrTempId);
  },

  /**
   * Upload an audio file to the 'question-audios' bucket.
   * Performs client-side format and size checks before uploading.
   */
  async uploadQuestionAudio(file: File, questionIdOrTempId?: string): Promise<UploadedMedia> {
    if (!isSupabaseConfigured || !supabase) {
      throw new Error("Supabase não está configurado. Não é possível fazer upload.");
    }

    // 1. Validation check
    const extension = "." + file.name.split(".").pop()?.toLowerCase();
    const allowedMimes = [
      "audio/mpeg", "audio/mp3", "audio/wav", "audio/x-wav", "audio/ogg", 
      "audio/mp4", "audio/x-m4a", "audio/aac", "audio/m4a"
    ];

    let isValidType = false;
    if (file.type) {
      isValidType = allowedMimes.includes(file.type) || file.type.startsWith("audio/");
    } else {
      isValidType = ALLOWED_AUDIO_EXTS.includes(extension);
    }

    // Secondary fallback validation
    if (!isValidType && ALLOWED_AUDIO_EXTS.includes(extension)) {
      isValidType = true;
    }

    if (!isValidType) {
      throw new Error("Formato de áudio não suportado. Use MP3, WAV, OGG, M4A ou AAC.");
    }

    if (file.size > MAX_AUDIO_SIZE) {
      throw new Error(`Áudio excede o tamanho máximo de 50MB. Tamanho do arquivo: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
    }

    return this.uploadFile(file, "question-audios", questionIdOrTempId);
  },

  /**
   * Internal generic file upload runner
   */
  async uploadFile(
    file: File,
    bucket: "question-images" | "question-audios",
    questionIdOrTempId?: string
  ): Promise<UploadedMedia> {
    if (!isSupabaseConfigured || !supabase) {
      throw new Error("Supabase não está configurado.");
    }

    const { data: userData, error: userError } = await supabase.auth.getUser();

    if (userError || !userData?.user) {
      throw new Error("Usuário não autenticado. Faça login novamente.");
    }

    const isAdmin = await adminService.isCurrentUserAdmin(userData.user);
    if (!isAdmin) {
      throw new Error("Apenas administradores podem enviar mídias de questões.");
    }

    const timestamp = Date.now();
    const safeName = getSafeFileName(file.name);
    const path = `${userData.user.id}/${timestamp}-${safeName}`;

    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file, {
        cacheControl: "3600",
        upsert: false,
        contentType: file.type || undefined
      });

    if (error) {
      console.error(`[MediaUpload] Supabase Storage upload error`, {
        message: error.message,
        name: error.name,
        bucket,
        path
      });
      if (error.message.includes("new row violates row-level security policy") || error.message.includes("policy")) {
        throw new Error("Não foi possível enviar o arquivo. Verifique se sua conta admin tem permissão para enviar mídias.");
      }
      throw new Error(`Erro ao enviar arquivo para o Supabase Storage: ${error.message}`);
    }

    const url = this.getPublicUrl(bucket, path);

    // Dynamic MIME type resolution for empty values
    let resolvedMime = file.type;
    if (!resolvedMime) {
      const ext = file.name.split(".").pop()?.toLowerCase();
      if (ext === "m4a") resolvedMime = "audio/mp4";
      else if (ext === "aac") resolvedMime = "audio/aac";
      else if (ext === "mp3") resolvedMime = "audio/mpeg";
      else if (ext === "wav") resolvedMime = "audio/wav";
      else if (ext === "ogg") resolvedMime = "audio/ogg";
      else resolvedMime = bucket === "question-images" ? "image/jpeg" : "audio/mpeg";
    }

    return {
      bucket,
      path,
      url,
      fileName: file.name,
      mimeType: resolvedMime,
      size: file.size,
    };
  },

  /**
   * Resolves public URL for a given bucket and path
   */
  getPublicUrl(bucket: string, path: string): string {
    if (!isSupabaseConfigured || !supabase) return "";
    const { data } = supabase.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl;
  },

  /**
   * Safely deletes an asset from Supabase Storage.
   * Silently returns on any error or missing values to ensure resilient flows.
   */
  async deleteQuestionMedia(path: string, bucket: "question-images" | "question-audios"): Promise<void> {
    if (!isSupabaseConfigured || !supabase || !path) return;
    try {
      const { error } = await supabase.storage.from(bucket).remove([path]);
      if (error) {
        console.warn(`Erro ao deletar mídia do bucket ${bucket} no path ${path}:`, error.message);
      }
    } catch (err) {
      console.warn("Falha silenciosa ao excluir mídia do storage:", err);
    }
  },
};
